import { useState, useEffect } from 'react';
import {
  getAilmentTemplates, createAilmentTemplate, updateAilmentTemplate, deactivateAilmentTemplate,
  resetAilmentTemplateToDefault, approveAilmentTemplate, rejectAilmentTemplate,
} from '../api';
import { useAuth } from '@/hooks/useAuth';
import type { AilmentTemplate, AilmentField, AilmentSection, ScoringRule, RuleCondition, TriageFieldKey } from '../types';
import { ALL_TRIAGE_FIELD_KEYS } from '../types';
import { cn } from '@/lib/cn';
import { COLOR_TONE, TONE_TEXT, renderChoiceIllustration } from '@/components/ui/choiceIllustration';
import type { MatrixColumn, MatrixRow, MatrixCell, PictorialOption } from '@/lib/assessmentTemplate';

const FIELD_TYPES = [
  { id: 'checkbox-group', label: 'Checkbox group (multi-select)' },
  { id: 'radio', label: 'Radio (single choice)' },
  { id: 'text', label: 'Free text' },
  { id: 'number', label: 'Number' },
  { id: 'vitals', label: 'Vitals row' },
  { id: 'due-checklist', label: 'Due checklist (recurring screenings)' },
  { id: 'condition-status', label: 'Condition status (No / Yes / Unknown per item)' },
  { id: 'history-table', label: 'Patient history table (diagnoses/procedures/drugs)' },
  { id: 'choice-matrix', label: 'Choice matrix (rows × shared scale, e.g. diet assessment)' },
  { id: 'pictorial-choice', label: 'Pictorial choice (single choice, illustrated cards)' },
];

const TRIAGE_FIELD_LABELS: Record<TriageFieldKey, string> = {
  risk_level: 'Risk level',
  pain_scale: 'Pain scale (0-10)',
  allergies: 'Known allergies',
  current_meds: 'Current medications',
};

const SCORABLE_TYPES = ['checkbox-group', 'radio', 'number', 'condition-status', 'choice-matrix', 'pictorial-choice'];
const COLORS = ['green', 'yellow', 'red'];
const COLOR_META: Record<string, { bg: string; text: string }> = {
  green: { bg: '#F0FDF4', text: '#065F46' }, yellow: { bg: '#FEF3C7', text: '#92400E' }, red: { bg: '#FEE2E2', text: '#991B1B' },
};

// Names the Phase 0 illustration set (components/ui/illustrations/) actually
// recognizes — renderChoiceIllustration() falls back to nothing (label only)
// for anything else, so the builder only ever offers names that will draw.
const ILLUSTRATION_OPTIONS = [
  { id: '', label: 'None' },
  { id: 'plate-1/3', label: 'Plate · ⅓ full' },
  { id: 'plate-1/2', label: 'Plate · ½ full' },
  { id: 'plate-2/3', label: 'Plate · ⅔ full' },
  { id: 'plate-full', label: 'Plate · full' },
  { id: 'pose-walking', label: 'Figure · walking' },
  { id: 'pose-lifting', label: 'Figure · lifting' },
  { id: 'pose-sitting', label: 'Figure · sitting' },
];
const HISTORY_SOURCES = [
  { id: 'diagnoses', label: 'Diagnoses / problem list (Year + Diagnosis)' },
  { id: 'procedures', label: 'Past procedures (Year + Procedure)' },
  { id: 'medications', label: 'Current drugs (Name + Dose + Frequency)' },
];
const OPERATORS = [
  { id: 'eq', label: '= (equals)' }, { id: 'in', label: 'is one of' },
  { id: 'includes', label: 'includes (multi-select)' }, { id: 'nonEmpty', label: 'is answered / non-empty' },
  { id: 'gt', label: '> (greater than)' }, { id: 'gte', label: '≥' }, { id: 'lt', label: '< (less than)' }, { id: 'lte', label: '≤' },
];

const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'field';

function emptyField(): AilmentField { return { type: 'checkbox-group', label: '', key: '', options: [] }; }
function emptySection(): AilmentSection { return { label: '', fields: [emptyField()] }; }
function emptyRule(): ScoringRule { return { mode: 'any', conditions: [{ field: '', op: 'nonEmpty', value: '' }], result: 'red' }; }

// backend/utils/assessmentScoring.js's scoreField() has no special case for
// pictorial-choice — it scores like any other option-colored field, via
// `field.option_colors` keyed by the stored answer (the option's `value`).
// Rather than making an admin colour every option twice (once on the card,
// once in a separate scoring table), synthesise `option_colors` from the
// options' own `color` at save time, same "author once, never disagree"
// posture docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §3 calls for.
function withSynthesizedOptionColors(sections: AilmentSection[]): AilmentSection[] {
  return sections.map((sec) => ({
    ...sec,
    fields: sec.fields.map((f) => {
      if (f.type !== 'pictorial-choice') return f;
      const options = (f.options as PictorialOption[] | undefined) || [];
      const option_colors: Record<string, string> = {};
      options.forEach((o) => { if (o.color) option_colors[o.value] = o.color; });
      return Object.keys(option_colors).length ? { ...f, option_colors } : f;
    }),
  }));
}

export default function AilmentTemplatesTab() {
  const { user } = useAuth();
  const isReviewer = user?.role === 'Executive Director';
  const [templates, setTemplates] = useState<AilmentTemplate[] | null>(null);
  const [editing, setEditing] = useState<AilmentTemplate | null>(null);
  const [error, setError] = useState('');
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');

  const load = () => getAilmentTemplates().then(setTemplates).catch((e: any) => setError(e.message));
  useEffect(() => { load(); }, []);

  const approve = async (t: AilmentTemplate) => {
    try { await approveAilmentTemplate(t.id!); await load(); } catch (e: any) { setError(e.message); }
  };
  const reject = async (t: AilmentTemplate) => {
    try { await rejectAilmentTemplate(t.id!, rejectNotes.trim()); setRejecting(null); setRejectNotes(''); await load(); } catch (e: any) { setError(e.message); }
  };

  const startNew = () => setEditing({ name: '', module_key: '', department: '', scoring_rules: [], fields_config: { sections: [emptySection()] } });
  const startEdit = (t: AilmentTemplate) => {
    const uiRules = (t.scoring_rules || []).map((r: any) => ({
      mode: r.all ? 'all' : 'any',
      conditions: r.all || r.any || [],
      result: r.result,
    }));
    setEditing(JSON.parse(JSON.stringify({ ...t, module_key: t.module_key ?? '', department: t.department ?? '', scoring_rules: uiRules })));
  };

  const toggleActive = async (t: AilmentTemplate) => {
    try {
      if (t.active) await deactivateAilmentTemplate(t.id!);
      else await updateAilmentTemplate(t.id!, { active: true });
      await load();
    } catch (e: any) { setError(e.message); }
  };

  if (editing) {
    return <TemplateEditor template={editing} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />;
  }

  if (!templates) return <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading templates…</div>;

  return (
    <div>
      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}
      <div style={{ marginBottom: 16 }}>
        <button className="btn-primary" onClick={startNew}>+ New assessment module</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {templates.map((t) => (
          <div key={t.id} className="card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {t.name}
                  {t.department && <span style={{ marginLeft: 8, fontWeight: 500, fontSize: 11, color: '#6366F1', background: '#EEF2FF', padding: '2px 8px', borderRadius: 8 }}>{t.department}</span>}
                  {t.module_key && <span style={{ marginLeft: 6, fontWeight: 500, fontSize: 11, color: '#6B7280', fontFamily: 'var(--mono)' }}>#{t.module_key}</span>}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  {t.fields_config.sections?.length || 0} sections · {t.fields_config.sections?.reduce((s, sec) => s + sec.fields.length, 0) || 0} fields
                  {t.scoring_rules?.length ? ` · ${t.scoring_rules.length} composite scoring rule${t.scoring_rules.length === 1 ? '' : 's'}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className={`badge ${t.active ? 'badge-success' : 'badge-gray'}`}>{t.active ? 'Active' : 'Inactive'}</span>
                <button className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => startEdit(t)}>Edit</button>
                <button className={t.active ? 'btn-danger' : 'btn-ghost'} style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => toggleActive(t)}>
                  {t.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </div>

            {t.pending_status === 'pending' && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: 12, color: '#92400E' }}>
                  ⏳ Pending changes submitted by <b>{t.pending_submitted_by}</b> on {new Date(t.pending_submitted_at!).toLocaleString('en-IN')} — awaiting Executive Director review.
                </div>
                {isReviewer && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn-success" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => approve(t)}>✓ Approve</button>
                    <button className="btn-danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => { setRejecting(t.id!); setRejectNotes(''); }}>✕ Reject</button>
                  </div>
                )}
                {rejecting === t.id && (
                  <div style={{ marginTop: 8 }}>
                    <textarea placeholder="Reason for rejection…" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                      style={{ resize: 'vertical', height: 60, width: '100%', fontSize: 12, marginBottom: 6 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => reject(t)}>Confirm reject</button>
                      <button className="btn-ghost" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => setRejecting(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {t.pending_status === 'rejected' && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)', fontSize: 12, color: '#991B1B' }}>
                ✕ Last edit rejected by <b>{t.pending_reviewed_by}</b>{t.pending_review_notes ? `: "${t.pending_review_notes}"` : ''}
              </div>
            )}
          </div>
        ))}
        {templates.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No assessment modules yet</div>}
      </div>
    </div>
  );
}

function TemplateEditor({ template, onCancel, onSaved }: { template: AilmentTemplate; onCancel: () => void; onSaved: () => Promise<void> }) {
  const [name, setName] = useState(template.name);
  const [moduleKey, setModuleKey] = useState(template.module_key || '');
  const [department, setDepartment] = useState(template.department || '');
  const [sections, setSections] = useState<AilmentSection[]>(template.fields_config.sections || []);
  const [scoringRules, setScoringRules] = useState<ScoringRule[]>(template.scoring_rules || []);
  // NULL/undefined on the template means "all four" (see migration_ailment_
  // triage_fields.sql) — resolved to the concrete default set here so the
  // checkboxes always have a definite starting state to show/toggle.
  const [triageFields, setTriageFields] = useState<TriageFieldKey[]>(template.triage_fields ?? ALL_TRIAGE_FIELD_KEYS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = template.id === undefined;

  const allFieldKeys = sections.flatMap((sec) => sec.fields.map((f) => f.key).filter(Boolean));

  const updateSection = (idx: number, patch: Partial<AilmentSection>) => setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, ...patch } : sec)));
  const moveSection = (idx: number, dir: number) => {
    const t = idx + dir; if (t < 0 || t >= sections.length) return;
    const next = [...sections]; [next[idx], next[t]] = [next[t]!, next[idx]!]; setSections(next);
  };
  const removeSection = (idx: number) => setSections((s) => s.filter((_, i) => i !== idx));
  const addSection = () => setSections((s) => [...s, emptySection()]);

  const updateField = (secIdx: number, fieldIdx: number, patch: Partial<AilmentField>) => setSections((s) => s.map((sec, i) => (i !== secIdx ? sec : {
    ...sec, fields: sec.fields.map((f, j) => (j !== fieldIdx ? f : { ...f, ...patch })),
  })));
  const moveField = (secIdx: number, fieldIdx: number, dir: number) => {
    const sec = sections[secIdx]!; const t = fieldIdx + dir;
    if (t < 0 || t >= sec.fields.length) return;
    const fields = [...sec.fields]; [fields[fieldIdx], fields[t]] = [fields[t]!, fields[fieldIdx]!];
    updateSection(secIdx, { fields });
  };
  const removeField = (secIdx: number, fieldIdx: number) => updateSection(secIdx, { fields: sections[secIdx]!.fields.filter((_, j) => j !== fieldIdx) });
  const addField = (secIdx: number) => updateSection(secIdx, { fields: [...sections[secIdx]!.fields, emptyField()] });

  const updateRule = (idx: number, patch: Partial<ScoringRule>) => setScoringRules((r) => r.map((rule, i) => (i !== idx ? rule : { ...rule, ...patch })));
  const removeRule = (idx: number) => setScoringRules((r) => r.filter((_, i) => i !== idx));
  const addRule = () => setScoringRules((r) => [...r, emptyRule()]);
  const updateCondition = (ruleIdx: number, condIdx: number, patch: Partial<RuleCondition>) => setScoringRules((r) => r.map((rule, i) => (i !== ruleIdx ? rule : {
    ...rule, conditions: rule.conditions.map((c, j) => (j !== condIdx ? c : { ...c, ...patch })),
  })));
  const addCondition = (ruleIdx: number) => updateRule(ruleIdx, { conditions: [...scoringRules[ruleIdx]!.conditions, { field: '', op: 'nonEmpty', value: '' }] });
  const removeCondition = (ruleIdx: number, condIdx: number) => updateRule(ruleIdx, { conditions: scoringRules[ruleIdx]!.conditions.filter((_, j) => j !== condIdx) });

  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  const save = async () => {
    if (!name.trim()) { setError('Module name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name: name.trim(),
        module_key: moduleKey.trim() || null,
        department: department.trim() || null,
        scoring_rules: scoringRules.length ? scoringRules.map((r) => ({ [r.mode]: r.conditions, result: r.result })) : null,
        // Only sent as null (falling back to "all four") when every triage
        // field is enabled — the common case — so a template nobody has
        // touched this setting on round-trips identically instead of
        // acquiring an explicit "all four" array the first time it's saved
        // for an unrelated reason.
        triage_fields: triageFields.length === ALL_TRIAGE_FIELD_KEYS.length ? null : triageFields,
        fields_config: { sections: withSynthesizedOptionColors(sections) },
      };
      if (isNew) await createAilmentTemplate(payload as unknown as Partial<AilmentTemplate>);
      else await updateAilmentTemplate(template.id!, payload as unknown as Partial<AilmentTemplate>);
      await onSaved();
    } catch (e: any) { setError(e.message); setSaving(false); }
  };

  const resetToDefault = async () => {
    setResetting(true); setError('');
    try {
      const updated = await resetAilmentTemplateToDefault(template.id!);
      setSections(updated.fields_config.sections || []);
      setTriageFields(updated.triage_fields ?? ALL_TRIAGE_FIELD_KEYS);
      setResetConfirm(false);
    } catch (e: any) { setError(e.message); }
    setResetting(false);
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{isNew ? 'New assessment module' : `Editing: ${template.name}`}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isNew && template.default_fields_config && !resetConfirm && (
            <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setResetConfirm(true)}>↺ Reset to default</button>
          )}
          {resetConfirm && (
            <span style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              Discard current content and restore the default?
              <button className="btn-danger" style={{ fontSize: 12, padding: '4px 10px' }} disabled={resetting} onClick={resetToDefault}>{resetting ? 'Resetting…' : 'Yes, reset'}</button>
              <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setResetConfirm(false)}>Cancel</button>
            </span>
          )}
          <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save module'}</button>
        </div>
      </div>

      {!isNew && (
        <div style={{ marginBottom: 14, padding: '8px 12px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 'var(--radius)', fontSize: 12, color: '#075985' }}>
          ℹ Saving here submits your changes for Executive Director review — the currently-approved version stays live for nurses until it is approved.
        </div>
      )}

      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div className="grid2">
          <div>
            <label className="lbl">Module name (e.g. "Diabetes Assessment", "Fever")</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="lbl">Department (optional, e.g. "Diabetology")</label>
            <input value={department} onChange={(e) => setDepartment(e.target.value)} />
          </div>
          <div>
            <label className="lbl">Module key (optional — set this to make it selectable in the Nurse-stage assessment picker, e.g. "diabetes")</label>
            <input value={moduleKey} onChange={(e) => setModuleKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} placeholder="e.g. diabetes, fever, heart" />
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Triage fields (shown before this module's own sections)</div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
          Risk level, pain scale, known allergies and current medications are collected on every Pre-Consult screen regardless of module —
          uncheck any that don't apply here (e.g. Pain scale for an assessment with no pain component).
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          {ALL_TRIAGE_FIELD_KEYS.map((key) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
              <input
                type="checkbox"
                checked={triageFields.includes(key)}
                onChange={(e) => setTriageFields((cur) => (e.target.checked ? [...cur, key] : cur.filter((k) => k !== key)))}
              />
              {TRIAGE_FIELD_LABELS[key]}
            </label>
          ))}
        </div>
      </div>

      {sections.map((sec, secIdx) => (
        <div key={secIdx} className="card" style={{ padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <input value={sec.label} onChange={(e) => updateSection(secIdx, { label: e.target.value })} placeholder="Section label (e.g. RED FLAGS) — leave blank for an unlabeled row" style={{ flex: 1, fontWeight: 600 }} />
            <label className="flex items-center gap-1.5 whitespace-nowrap text-[11px] text-ink-muted" title="Renders as a 2-column-wide card in Pre-Consult's layout (a hint only — below xl it's always full width)">
              <input
                type="checkbox"
                checked={sec.layout?.span === 2}
                onChange={(e) => updateSection(secIdx, { layout: e.target.checked ? { span: 2 } : undefined })}
              />
              Wide section
            </label>
            <button className="btn-ghost" style={{ padding: '4px 8px' }} onClick={() => moveSection(secIdx, -1)} disabled={secIdx === 0}>▲</button>
            <button className="btn-ghost" style={{ padding: '4px 8px' }} onClick={() => moveSection(secIdx, +1)} disabled={secIdx === sections.length - 1}>▼</button>
            <button className="btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => removeSection(secIdx)}>✕ Section</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sec.fields.map((field, fieldIdx) => (
              <FieldRow key={fieldIdx} field={field}
                otherFieldKeys={allFieldKeys.filter((k) => k !== field.key)}
                onChange={(patch) => updateField(secIdx, fieldIdx, patch)}
                onMoveUp={() => moveField(secIdx, fieldIdx, -1)} onMoveDown={() => moveField(secIdx, fieldIdx, +1)}
                onRemove={() => removeField(secIdx, fieldIdx)}
                canMoveUp={fieldIdx !== 0} canMoveDown={fieldIdx !== sec.fields.length - 1} />
            ))}
          </div>
          <button className="btn-ghost" style={{ marginTop: 10, fontSize: 12 }} onClick={() => addField(secIdx)}>+ Add field</button>
        </div>
      ))}

      <button className="btn-ghost" onClick={addSection} style={{ marginBottom: 20 }}>+ Add section</button>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Overall risk scoring (optional)</div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
          Composite rules for an overall Green/Yellow/Red that depends on more than one field at once — e.g. Fever's "any red flag checked → Red" regardless of what any single field shows. Evaluated top-to-bottom; first match wins.
        </div>
        {scoringRules.map((rule, ruleIdx) => (
          <div key={ruleIdx} style={{ padding: '10px 12px', background: '#F9FAFB', borderRadius: 'var(--radius)', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12 }}>If</span>
              <select value={rule.mode} onChange={(e) => updateRule(ruleIdx, { mode: e.target.value as 'all' | 'any' })} style={{ fontSize: 12, width: 80 }}>
                <option value="any">ANY</option><option value="all">ALL</option>
              </select>
              <span style={{ fontSize: 12 }}>of these match →</span>
              <select value={rule.result} onChange={(e) => updateRule(ruleIdx, { result: e.target.value })} style={{ fontSize: 12 }}>
                {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div style={{ flex: 1 }} />
              <button className="btn-danger" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeRule(ruleIdx)}>✕ Rule</button>
            </div>
            {rule.conditions.map((cond, condIdx) => (
              <div key={condIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 1fr auto', gap: 6, marginBottom: 6 }}>
                <select value={cond.field} onChange={(e) => updateCondition(ruleIdx, condIdx, { field: e.target.value })} style={{ fontSize: 12 }}>
                  <option value="">Select field…</option>
                  {allFieldKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <select value={cond.op} onChange={(e) => updateCondition(ruleIdx, condIdx, { op: e.target.value })} style={{ fontSize: 12 }}>
                  {OPERATORS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                {cond.op !== 'nonEmpty' ? (
                  <input placeholder="Value (comma-separated for 'is one of')" value={cond.value}
                    onChange={(e) => updateCondition(ruleIdx, condIdx, { value: cond.op === 'in' ? e.target.value.split(',').map((s) => s.trim()) : e.target.value })}
                    style={{ fontSize: 12 }} />
                ) : <div />}
                <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeCondition(ruleIdx, condIdx)}>✕</button>
              </div>
            ))}
            <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => addCondition(ruleIdx)}>+ Add condition</button>
          </div>
        ))}
        <button className="btn-ghost" style={{ fontSize: 12 }} onClick={addRule}>+ Add scoring rule</button>
      </div>
    </div>
  );
}

interface FieldRowProps {
  field: AilmentField;
  otherFieldKeys: string[];
  onChange: (patch: Partial<AilmentField>) => void;
  onMoveUp: () => void; onMoveDown: () => void; onRemove: () => void;
  canMoveUp: boolean; canMoveDown: boolean;
}

function FieldRow({ field, otherFieldKeys, onChange, onMoveUp, onMoveDown, onRemove, canMoveUp, canMoveDown }: FieldRowProps) {
  const [showScoring, setShowScoring] = useState(false);
  const isScorable = SCORABLE_TYPES.includes(field.type);
  const isSelectLike = field.type === 'checkbox-group' || field.type === 'radio';

  const [showVisibility, setShowVisibility] = useState(!!field.visible_if);
  const visibleIf = field.visible_if || { mode: 'any' as const, conditions: [{ field: '', op: 'nonEmpty', value: '' }] };
  const setVisibleIf = (patch: Partial<typeof visibleIf>) => onChange({ visible_if: { ...visibleIf, ...patch } });
  const toggleVisibility = (on: boolean) => { setShowVisibility(on); onChange({ visible_if: on ? visibleIf : null }); };
  const updateVisCondition = (idx: number, patch: Partial<RuleCondition>) => setVisibleIf({ conditions: visibleIf.conditions.map((c, i) => (i !== idx ? c : { ...c, ...patch })) });
  const addVisCondition = () => setVisibleIf({ conditions: [...visibleIf.conditions, { field: '', op: 'nonEmpty', value: '' }] });
  const removeVisCondition = (idx: number) => setVisibleIf({ conditions: visibleIf.conditions.filter((_, i) => i !== idx) });

  const optionColors = field.option_colors || {};
  const setOptionColor = (opt: string, color: string) => onChange({ option_colors: { ...optionColors, [opt]: color || undefined } as Record<string, string> });

  const thresholds = field.thresholds || [];
  const updateThreshold = (idx: number, patch: Partial<{ max?: number; color: string }>) => onChange({ thresholds: thresholds.map((t, i) => (i !== idx ? t : { ...t, ...patch })) });
  const addThreshold = () => onChange({ thresholds: [...thresholds, { max: 0, color: 'green' }] });
  const removeThreshold = (idx: number) => onChange({ thresholds: thresholds.filter((_, i) => i !== idx) });

  const items = field.items || [];
  const updateItem = (idx: number, patch: Partial<{ label: string; interval_months: number }>) => onChange({ items: items.map((it, i) => (i !== idx ? it : { ...it, ...patch })) });
  const addItem = () => onChange({ items: [...items, { label: '', interval_months: 12 }] });
  const removeItem = (idx: number) => onChange({ items: items.filter((_, i) => i !== idx) });

  const matrixColumns = field.columns || [];
  const updateColumn = (idx: number, patch: Partial<MatrixColumn>) => onChange({ columns: matrixColumns.map((c, i) => (i !== idx ? c : { ...c, ...patch })) });
  const addColumn = () => onChange({ columns: [...matrixColumns, { value: '', label: '' }] });
  const removeColumn = (idx: number) => onChange({ columns: matrixColumns.filter((_, i) => i !== idx) });

  const matrixRows = field.rows || [];
  const updateRow = (idx: number, patch: Partial<MatrixRow>) => onChange({ rows: matrixRows.map((r, i) => (i !== idx ? r : { ...r, ...patch })) });
  const addRow = () => onChange({ rows: [...matrixRows, { key: '', label: '', cells: {} }] });
  const removeRow = (idx: number) => onChange({ rows: matrixRows.filter((_, i) => i !== idx) });
  const updateCell = (rowIdx: number, colValue: string, patch: Partial<MatrixCell>) => {
    const row = matrixRows[rowIdx];
    if (!row || !colValue) return;
    const cell = row.cells[colValue] || { option: '' };
    updateRow(rowIdx, { cells: { ...row.cells, [colValue]: { ...cell, ...patch } } });
  };

  const pictorialOptions = (field.options as PictorialOption[] | undefined) || [];
  const updateOption = (idx: number, patch: Partial<PictorialOption>) => onChange({ options: pictorialOptions.map((o, i) => (i !== idx ? o : { ...o, ...patch })) });
  const addOption = () => onChange({ options: [...pictorialOptions, { value: '', label: '' }] });
  const removeOption = (idx: number) => onChange({ options: pictorialOptions.filter((_, i) => i !== idx) });

  return (
    <div style={{ padding: '8px 10px', background: '#F9FAFB', borderRadius: 'var(--radius)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr auto', gap: 8, alignItems: 'start' }}>
        <select value={field.type} onChange={(e) => onChange({ type: e.target.value })} style={{ fontSize: 12 }}>
          {FIELD_TYPES.map((ft) => <option key={ft.id} value={ft.id}>{ft.label}</option>)}
        </select>
        <input placeholder="Field label (e.g. Gen, Character of pain)" value={field.label}
          onChange={(e) => onChange({ label: e.target.value, key: field.key || slugify(e.target.value) })} style={{ fontSize: 12 }} />
        {(field.type === 'checkbox-group' || field.type === 'radio' || field.type === 'vitals') ? (
          <input placeholder="Options, comma-separated (e.g. Headache, Myalgia, Chills)"
            value={(field.options || []).join(', ')}
            onChange={(e) => onChange({ options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            style={{ fontSize: 12 }} />
        ) : field.type === 'number' ? (
          <input placeholder="Suffix (e.g. /10, d, %)" value={field.suffix || ''} onChange={(e) => onChange({ suffix: e.target.value })} style={{ fontSize: 12 }} />
        ) : field.type === 'history-table' ? (
          <select value={field.history_source || ''} onChange={(e) => onChange({ history_source: e.target.value })} style={{ fontSize: 12 }}>
            <option value="">Select source…</option>
            {HISTORY_SOURCES.map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
          </select>
        ) : field.type === 'choice-matrix' || field.type === 'pictorial-choice' ? (
          <div className="flex items-center text-[11px] text-ink-muted">Configure rows/columns or options below ↓</div>
        ) : <div />}
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn-ghost" style={{ padding: '4px 6px' }} onClick={onMoveUp} disabled={!canMoveUp}>▲</button>
          <button className="btn-ghost" style={{ padding: '4px 6px' }} onClick={onMoveDown} disabled={!canMoveDown}>▼</button>
          <button className="btn-danger" style={{ padding: '4px 8px', fontSize: 12 }} onClick={onRemove}>✕</button>
        </div>
      </div>

      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #E5E7EB' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={showVisibility} onChange={(e) => toggleVisibility(e.target.checked)} disabled={!otherFieldKeys?.length} />
          Only show this field if… {!otherFieldKeys?.length && <span style={{ color: '#9CA3AF' }}>(no other fields to reference yet)</span>}
        </label>
        {showVisibility && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12 }}>Match</span>
              <select value={visibleIf.mode} onChange={(e) => setVisibleIf({ mode: e.target.value as 'all' | 'any' })} style={{ fontSize: 12, width: 80 }}>
                <option value="any">ANY</option><option value="all">ALL</option>
              </select>
              <span style={{ fontSize: 12 }}>of these:</span>
            </div>
            {visibleIf.conditions.map((cond, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 1fr auto', gap: 6, marginBottom: 6 }}>
                <select value={cond.field} onChange={(e) => updateVisCondition(idx, { field: e.target.value })} style={{ fontSize: 12 }}>
                  <option value="">Select field…</option>
                  {otherFieldKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <select value={cond.op} onChange={(e) => updateVisCondition(idx, { op: e.target.value })} style={{ fontSize: 12 }}>
                  {OPERATORS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                {cond.op !== 'nonEmpty' ? (
                  <input placeholder="Value (comma-separated for 'is one of')" value={cond.value}
                    onChange={(e) => updateVisCondition(idx, { value: cond.op === 'in' ? e.target.value.split(',').map((s) => s.trim()) : e.target.value })}
                    style={{ fontSize: 12 }} />
                ) : <div />}
                <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeVisCondition(idx)}>✕</button>
              </div>
            ))}
            <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={addVisCondition}>+ Add condition</button>
          </div>
        )}
      </div>

      {field.type === 'due-checklist' && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #E5E7EB' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Screening items (each tracked as Green=done / Yellow=due soon / Red=overdue)</div>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 140px auto', gap: 6, marginBottom: 6 }}>
              <input placeholder="e.g. HbA1c, Eye Examination" value={item.label} onChange={(e) => updateItem(idx, { label: e.target.value })} style={{ fontSize: 12 }} />
              <input type="number" placeholder="Interval (months)" value={item.interval_months} onChange={(e) => updateItem(idx, { interval_months: Number(e.target.value) })} style={{ fontSize: 12 }} />
              <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeItem(idx)}>✕</button>
            </div>
          ))}
          <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={addItem}>+ Add screening item</button>
        </div>
      )}

      {field.type === 'condition-status' && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #E5E7EB' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>
            Conditions (each answered as a fixed No / Yes / Unknown — not admin-colorable per item, unlike a checkbox group)
          </div>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, marginBottom: 6 }}>
              <input placeholder="e.g. Retinopathy, CAD" value={item.label} onChange={(e) => updateItem(idx, { label: e.target.value })} style={{ fontSize: 12 }} />
              <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeItem(idx)}>✕</button>
            </div>
          ))}
          <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => onChange({ items: [...items, { label: '' }] })}>+ Add condition</button>
        </div>
      )}

      {field.type === 'choice-matrix' && (
        <div className="mt-2 border-t border-dashed border-x-0 border-b-0 border-line pt-2">
          <div className="mb-1.5 text-[11px] font-semibold text-ink-muted">
            Columns (the shared scale every row is answered against, e.g. Good / Partial / Poor)
          </div>
          <div className="mb-2 flex flex-col gap-1.5">
            {matrixColumns.map((col, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_110px_auto] items-center gap-1.5">
                <input className="text-xs" placeholder="Column label (e.g. GOOD (PASS))" value={col.label}
                  onChange={(e) => updateColumn(idx, { label: e.target.value, value: col.value || slugify(e.target.value) })} />
                <input className="text-xs" placeholder="Value key (auto)" value={col.value} onChange={(e) => updateColumn(idx, { value: e.target.value })} />
                <select className="text-xs" value={col.color || ''} onChange={(e) => updateColumn(idx, { color: (e.target.value || undefined) as MatrixColumn['color'] })}>
                  <option value="">— colour —</option>
                  {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="btn-ghost px-2 text-[11px]" onClick={() => removeColumn(idx)}>✕</button>
              </div>
            ))}
          </div>
          <button className="btn-ghost px-2 py-0.5 text-[11px]" onClick={addColumn}>+ Add column</button>

          <div className="mb-1.5 mt-3 text-[11px] font-semibold text-ink-muted">
            Rows (the subjects asked about, e.g. Protein / Fibre / Carbohydrate)
          </div>
          <div className="flex flex-col gap-2.5">
            {matrixRows.map((row, rIdx) => (
              <div key={rIdx} className="rounded-[var(--radius)] border border-line p-2">
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-1.5">
                  <input className="text-xs" placeholder="Row label (e.g. Protein)" value={row.label}
                    onChange={(e) => updateRow(rIdx, { label: e.target.value, key: row.key || slugify(e.target.value) })} />
                  <input className="text-xs" placeholder="Row key (auto)" value={row.key} onChange={(e) => updateRow(rIdx, { key: e.target.value })} />
                  <input className="text-xs" placeholder="Cut-off text (optional, e.g. ≥ 1/3 plate)" value={row.cutoff || ''} onChange={(e) => updateRow(rIdx, { cutoff: e.target.value })} />
                  <button className="btn-ghost px-2 text-[11px]" onClick={() => removeRow(rIdx)}>✕ Row</button>
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  <input className="text-xs" placeholder="Hint (optional, e.g. egg, milk/curd, dal…)" value={row.hint || ''} onChange={(e) => updateRow(rIdx, { hint: e.target.value })} />
                  <input className="text-xs" placeholder="Summary tile label (optional, defaults to row label)" value={row.summary_label || ''} onChange={(e) => updateRow(rIdx, { summary_label: e.target.value })} />
                </div>

                {matrixColumns.length === 0 ? (
                  <div className="mt-1.5 text-[11px] text-ink-muted">Add at least one column above to configure this row's cells.</div>
                ) : (
                  <div className="mt-2 flex flex-col gap-1.5">
                    {matrixColumns.map((col) => {
                      const cell = row.cells[col.value];
                      const tone = COLOR_TONE[cell?.color || col.color || 'yellow'];
                      return (
                        <div key={col.value} className="grid grid-cols-[90px_1fr_150px_28px] items-center gap-1.5">
                          <span className={cn('truncate text-[10.5px] font-semibold', col.color ? TONE_TEXT[COLOR_TONE[col.color]] : 'text-ink-muted')}>
                            {col.label || col.value || '(unnamed column)'}
                          </span>
                          <input className="text-xs" placeholder="Answer text for this cell (e.g. All 3 meals)" value={cell?.option || ''}
                            onChange={(e) => updateCell(rIdx, col.value, { option: e.target.value })} />
                          <select className="text-xs" value={cell?.illustration || ''} onChange={(e) => updateCell(rIdx, col.value, { illustration: e.target.value || undefined })}>
                            {ILLUSTRATION_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                          </select>
                          <span aria-hidden="true" className="flex items-center justify-center">
                            {cell?.illustration ? renderChoiceIllustration(cell.illustration, tone, 'sm') : null}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <button className="btn-ghost mt-1.5 px-2 py-0.5 text-[11px]" onClick={addRow}>+ Add row</button>

          <input className="mt-3 w-full text-xs" placeholder="Footnote (optional, e.g. the diet matrix's pass-level guideline)" value={field.footnote || ''} onChange={(e) => onChange({ footnote: e.target.value })} />

          {(matrixColumns.length === 0 || matrixRows.length === 0) && (
            <div className="mt-2 rounded-lg bg-warn-soft px-2.5 py-1.5 text-[11px] text-warn-ink">
              ⚠ Needs at least one column and one row — until both exist, this question renders as free text in Pre-Consult, not a matrix.
            </div>
          )}
        </div>
      )}

      {field.type === 'pictorial-choice' && (
        <div className="mt-2 border-t border-dashed border-x-0 border-b-0 border-line pt-2">
          <div className="mb-1.5 text-[11px] font-semibold text-ink-muted">
            Options (each answer shown as an illustrated card, e.g. Physical Activity's ≥150 min/week · Some activity · Sedentary)
          </div>
          <div className="flex flex-col gap-2">
            {pictorialOptions.map((opt, idx) => (
              <div key={idx} className="rounded-[var(--radius)] border border-line p-2">
                <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-1.5">
                  <input className="text-xs" placeholder="Label (e.g. ≥ 150 min / week)" value={opt.label}
                    onChange={(e) => updateOption(idx, { label: e.target.value, value: opt.value || e.target.value })} />
                  <input className="text-xs" placeholder="Sub-label (optional, e.g. Good)" value={opt.sublabel || ''} onChange={(e) => updateOption(idx, { sublabel: e.target.value })} />
                  <button className="btn-ghost px-2 text-[11px]" onClick={() => removeOption(idx)}>✕</button>
                </div>
                <div className="mt-1.5 grid grid-cols-[1fr_150px_110px] items-center gap-1.5">
                  <input className="text-xs" placeholder="Stored value (auto from label)" value={opt.value} onChange={(e) => updateOption(idx, { value: e.target.value })} />
                  <select className="text-xs" value={opt.illustration || ''} onChange={(e) => updateOption(idx, { illustration: e.target.value || undefined })}>
                    {ILLUSTRATION_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <select className="text-xs" value={opt.color || ''} onChange={(e) => updateOption(idx, { color: (e.target.value || undefined) as PictorialOption['color'] })}>
                    <option value="">— colour —</option>
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {opt.illustration && (
                  <div className="mt-1.5">{renderChoiceIllustration(opt.illustration, COLOR_TONE[opt.color || 'yellow'], 'sm')}</div>
                )}
              </div>
            ))}
          </div>
          <button className="btn-ghost mt-1.5 px-2 py-0.5 text-[11px]" onClick={addOption}>+ Add option</button>

          {pictorialOptions.length === 0 && (
            <div className="mt-2 rounded-lg bg-warn-soft px-2.5 py-1.5 text-[11px] text-warn-ink">
              ⚠ No options configured yet — this question renders as free text in Pre-Consult until at least one option is added.
            </div>
          )}
        </div>
      )}

      {isScorable && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #E5E7EB' }}>
          <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setShowScoring((s) => !s)}>
            {showScoring ? '▲ Hide scoring' : '⚙ Scoring / summary'}
          </button>
          {showScoring && (
            <div style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 8 }}>
                <input type="checkbox" checked={!!field.contributes_to_summary} onChange={(e) => onChange({ contributes_to_summary: e.target.checked })} />
                Include in auto-summary card
              </label>
              {field.contributes_to_summary && (
                <input placeholder="Summary label (defaults to field label)" value={field.summary_label || ''}
                  onChange={(e) => onChange({ summary_label: e.target.value })} style={{ fontSize: 12, marginBottom: 8, width: '100%' }} />
              )}

              {field.type === 'choice-matrix' && field.contributes_to_summary && (
                <label className="mb-2 flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={!!field.summary_rows} onChange={(e) => onChange({ summary_rows: e.target.checked })} />
                  One summary tile per row (e.g. separate Protein / Fibre / Carb tiles) instead of one tile for the whole field
                </label>
              )}

              {isSelectLike && (field.options || []).length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6 }}>Per-option color:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(field.options as string[]).map((opt) => (
                      <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: optionColors[opt] ? COLOR_META[optionColors[opt]!]!.bg : '#fff', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                        <span style={{ fontSize: 11, color: optionColors[opt] ? COLOR_META[optionColors[opt]!]!.text : '#374151' }}>{opt}</span>
                        <select value={optionColors[opt] || ''} onChange={(e) => setOptionColor(opt, e.target.value)} style={{ fontSize: 11, padding: '1px 2px' }}>
                          <option value="">—</option>
                          {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {field.type === 'number' && (
                <div>
                  <div style={{ fontSize: 11, color: '#6B7280', margin: '8px 0 6px' }}>Thresholds (checked in order — first "value ≤ max" wins; leave last row's max blank as the catch-all):</div>
                  {thresholds.map((t, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 120px auto', gap: 6, marginBottom: 6 }}>
                      <select value={t.color} onChange={(e) => updateThreshold(idx, { color: e.target.value })} style={{ fontSize: 12 }}>
                        {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="number" placeholder="max value" value={t.max ?? ''} onChange={(e) => updateThreshold(idx, { max: e.target.value === '' ? undefined : Number(e.target.value) })} style={{ fontSize: 12 }} />
                      <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeThreshold(idx)}>✕</button>
                    </div>
                  ))}
                  <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={addThreshold}>+ Add threshold</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
